import React from "react";
import {
  Button,
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  Fade,
  Slide,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import part1 from "../media/part1.png";
import emailPng from "../media/Email_PNG.png";
import droneDelivery from "../media/drone_delivery_PNG.png";
import cloudData from "../media/Cloud_database_PNG.png";
import dataAnalysis from "../media/Data_analysis_PNG.png";

import HomeFooter from "./shared/HomeFooter";
import Navigation from "./shared/Navigation";

function Home(props) {
  const { isAuthenticated } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  React.useEffect(() => {
    // Set document title for the home page
    document.title = "ShieldShot - Protect Your Digital Content";
    
    // Store original styles to restore later
    const originalBodyBg = document.body.style.backgroundColor;
    const originalDocBg = document.documentElement.style.backgroundColor;
    
    // Apply blue background only for home page
    document.body.style.backgroundColor = "#2997ff";
    document.documentElement.style.backgroundColor = "#2997ff";
    
    // Cleanup function to restore original background when leaving home page
    return () => {
      document.body.style.backgroundColor = originalBodyBg;
      document.documentElement.style.backgroundColor = originalDocBg;
    };
  }, []);
  
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#2997ff",
        pb: 0,
        width: "100vw",
        position: "relative",
        overflow: "hidden",
        left: 0,
        top: 0,
        margin: 0,
        boxSizing: "border-box",
        padding: 0,
      }}
    >
      <Navigation 
        isAuthenticated={isAuthenticated}
      />

      {/* Hero Section */}
      <Container
        id="home"
        maxWidth="lg"
        sx={{
          pt: { xs: 4, sm: 6, md: 12 },
          pb: { xs: 2, sm: 4, md: 8 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4 }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: "white",
                borderRadius: { xs: 4, sm: 5, md: 6 },
                p: { xs: 2, sm: 3, md: 5 },
                boxShadow: 3,
                textAlign: "center",
                mb: { xs: 2, md: 0 },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#2997ff",
                  fontWeight: 500,
                  mb: 1,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                }}
              >
                Selling collections online?
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "#2997ff",
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "3rem" },
                }}
              >
                Protect your Images.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "grey.700",
                  mb: 2,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                }}
              >
                and distribute them automatically with ShieldShot
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "grey.700",
                  mb: 3,
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                  lineHeight: 1.4,
                }}
              >
                ShieldShot protects your photos with steganography watermarking and provides secure, personalized download links. Get peace of mind knowing your content is protected.
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  color: "#2997ff",
                  borderColor: "#2997ff",
                  borderRadius: "30px",
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                }}
                onClick={() => {
                  localStorage.clear();
                  navigate("/login?mode=signup");
                }}
              >
                Get Started
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src={part1}
              alt="Camera"
              sx={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: { xs: 2, sm: 3, md: 4 },
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                maxHeight: { xs: "250px", sm: "300px", md: "none" },
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ background: "white", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
              <Typography
                variant="h4"
                sx={{
                  color: "#2997ff",
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                Automate your reward distribution!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "grey.600",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Streamline your workflow with our four-step process
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {[
              {
                image: dataAnalysis,
                title: "Create your Tiers",
                description: "Set up subscription tiers and pricing for your content distribution.",
                step: "01"
              },
              {
                image: cloudData,
                title: "Upload your Collections",
                description: "Securely upload and organize your photo collections in the cloud.",
                step: "02"
              },
              {
                image: emailPng,
                title: "Import your Email List",
                description: "Connect your subscriber base for automated content delivery.",
                step: "03"
              },
              {
                image: droneDelivery,
                title: "Schedule Distribution",
                description: "Set up automated delivery schedules for your content releases.",
                step: "04"
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <Slide direction="up" in timeout={800 + index * 200}>
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 4px 20px rgba(41, 151, 255, 0.1)",
                      textAlign: "center",
                      p: { xs: 2, md: 3 },
                      pt: { xs: 2, md: 3 }, // Reduced top padding since step badge is now inside
                      height: "100%",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 8px 40px rgba(41, 151, 255, 0.2)",
                      },
                    }}
                  >
                    {/* Step number - now inside the box */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        background: "#2997ff",
                        color: "white",
                        borderRadius: "50%",
                        width: { xs: 32, md: 40 },
                        height: { xs: 32, md: 40 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "0.8rem", md: "1rem" },
                        fontWeight: 700,
                        boxShadow: "0 2px 8px rgba(41, 151, 255, 0.3)",
                        zIndex: 1,
                      }}
                    >
                      {feature.step}
                    </Box>
                    
                    <CardMedia
                      component="img"
                      image={feature.image}
                      alt={feature.title}
                      sx={{ 
                        height: { xs: 110, sm: 130, md: 150 }, 
                        width: "auto",
                        maxWidth: "85%",
                        objectFit: "contain", 
                        mb: { xs: 2, md: 3 },
                        mx: "auto",
                        display: "block",
                        mt: { xs: 6, md: 7 }, // More space for larger step badge and content separation
                      }}
                    />
                    <CardContent sx={{ pt: 0, px: { xs: 1, md: 2 }, pb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#2997ff",
                          fontWeight: 700,
                          mb: { xs: 1.5, md: 2 },
                          fontSize: { xs: "1.1rem", md: "1.25rem" },
                          mt: 1, // Add space from image
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "grey.600",
                          lineHeight: 1.6,
                          fontSize: { xs: "0.9rem", md: "0.95rem" },
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ background: "linear-gradient(135deg, #2997ff 0%, #1976d2 100%)", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", color: "white" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: "2rem", md: "2.5rem" },
                color: "white",
              }}
            >
              Ready to protect your content?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                maxWidth: 500,
                mx: "auto",
                color: "white",
              }}
            >
              Join thousands of creators who trust ShieldShot to secure their digital content
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: "white",
                  color: "#2997ff",
                  borderRadius: "30px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  boxShadow: 0,
                  "&:hover": {
                    background: "rgba(255,255,255,0.9)",
                    boxShadow: 2,
                  },
                }}
                onClick={() => {
                  localStorage.clear();
                  navigate("/login?mode=signup");
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  color: "white",
                  borderColor: "white",
                  borderRadius: "30px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
                onClick={() => navigate("/about")}
              >
                Learn More
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 0, backgroundColor: "#f1f1f1", py: 0 }}>
        <HomeFooter />
      </Box>
    </Box>
  );
}

export default Home;
